import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TruncatedText } from "@/components/ui/truncated-text";
import { Loader2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { useUpdateLandscapeSchedule } from "@/resources/landscape-schedule";
import type { UpcomingLandscapeSchedule } from "@/types";
import { useScheduleForm } from "@/hooks/useScheduleForm";
import { ScheduleFormFields } from "@/components/shared/ScheduleFormFields";
import {
  getCurrentHourInTz,
  getLocalDateString,
  isSameDateInTz,
} from "@/utils/schedule.util";

interface EditScheduleTimingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schedule: UpcomingLandscapeSchedule;
}

function computeInitialStartDate(schedule: UpcomingLandscapeSchedule): Date {
  const parsed = parseISO(schedule.startDate);
  const todayStr = getLocalDateString(new Date(), schedule.timezone);
  const parsedStr = getLocalDateString(parsed, schedule.timezone);
  if (parsedStr >= todayStr) return parsed;
  return new Date();
}

function computeInitialTimeOfDay(
  schedule: UpcomingLandscapeSchedule,
  initialStartDate: Date
): string {
  try {
    const currentHour = getCurrentHourInTz(schedule.timezone);
    const isToday = isSameDateInTz(initialStartDate, new Date(), schedule.timezone);
    if (!isToday) return schedule.timeOfDay;
    const scheduledHour = Number(schedule.timeOfDay.split(":")[0]);
    if (scheduledHour > currentHour) return schedule.timeOfDay;
    const nextHour = currentHour + 1;
    return nextHour <= 23 ? `${String(nextHour).padStart(2, "0")}:00` : "00:00";
  } catch {
    return schedule.timeOfDay;
  }
}

export function EditScheduleTimingModal({
  open,
  onOpenChange,
  schedule,
}: EditScheduleTimingModalProps) {
  const initialStartDate = computeInitialStartDate(schedule);
  const initialTimeOfDay = computeInitialTimeOfDay(schedule, initialStartDate);

  const form = useScheduleForm({
    initialTimezone: schedule.timezone,
    initialStartDate,
    initialTimeOfDay,
    initialEndDate: schedule.endDate ? parseISO(schedule.endDate) : undefined,
    initialFrequency: schedule.frequency ?? "weekly",
    initialDayOfWeek: schedule.dayOfWeek ?? "monday",
    initialDayOfMonth: schedule.dayOfMonth != null ? String(schedule.dayOfMonth) : "1",
  });

  const { mutate: updateSchedule, isPending } = useUpdateLandscapeSchedule();

  const handleSave = () => {
    updateSchedule(
      {
        id: schedule.id,
        payload: {
          ...(schedule.scheduleType === "recurring" && {
            frequency: form.frequency as "daily" | "weekly" | "biweekly" | "monthly",
            dayOfWeek:
              form.frequency === "weekly" || form.frequency === "biweekly"
                ? form.dayOfWeek
                : undefined,
            dayOfMonth: form.frequency === "monthly" ? Number(form.dayOfMonth) : undefined,
          }),
          timeOfDay: form.timeOfDay,
          startDate: format(form.startDate, "yyyy-MM-dd"),
          timezone: form.timezone,
          endDate: form.endDate ? format(form.endDate, "yyyy-MM-dd") : undefined,
        },
      },
      {
        onSuccess: () => {
          toast.success("Schedule updated");
          onOpenChange(false);
        },
        onError: () => {
          toast.error("Failed to update schedule");
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Edit Schedule Timing</DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            <TruncatedText fullValue={schedule.landscapePhrase}>
              &ldquo;{schedule.landscapePhrase}&rdquo;
            </TruncatedText>
          </p>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <ScheduleFormFields
            values={{
              frequency: form.frequency,
              dayOfWeek: form.dayOfWeek,
              dayOfMonth: form.dayOfMonth,
              startDate: form.startDate,
              timeOfDay: form.timeOfDay,
              timezone: form.timezone,
              endDate: form.endDate,
            }}
            scheduleType={schedule.scheduleType}
            showFrequency={schedule.scheduleType === "recurring"}
            timezoneOptions={form.timezoneOptions}
            availableHours={form.availableHours}
            isDateDisabled={form.isDateDisabled}
            onFrequencyChange={form.setFrequency}
            onDayOfWeekChange={form.setDayOfWeek}
            onDayOfMonthChange={form.setDayOfMonth}
            onStartDateChange={form.handleStartDateChange}
            onTimeOfDayChange={form.setTimeOfDay}
            onTimezoneChange={form.handleTimezoneChange}
            onEndDateChange={form.setEndDate}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
